import React from 'react';
import Text from 'antd/lib/typography/Text';
import styles from '../../styles/timelineStyles';
import { antGrey } from '../../styles/globalStyles';
import { Route, RouteComponentProps, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { AppComponentProps, ReleaseRouteComponentMatchParams } from '../../../typescriptHelpers';
import routes from '../../../routes';
import {
    BlockOutlined,
    CalendarFilled,
    SignalFilled,
    CalendarOutlined,
    AppstoreAddOutlined,
    FlagFilled,
    CaretRightOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import {
    selectAppName,
    selectAppPlatform,
    selectBuildDistributionBucketsForAppId,
    selectAppStoreType,
    selectIsBuildDistroEnabled,
    selectAppSettings,
} from '../../../selectors/selectors';
import { displayTextForAppPlatform } from '../../../helpers/displayHelpers';
import { useIsMobile } from '../../../mediaQueriesHelpers';
import {
    createAppBuildsRoute,
    createAppFeatureFlagsRoute,
    createAppOverviewRoute,
    createAppRolloutsRoute,
    createAppReleasesRoute,
    createAppBuildDistroBucketRoute,
    createAppSharedWithMeBucketRoute,
    createAppReleaseCalendarRoute,
    ROUTER_REDIRECT,
} from '../../../helpers/routeHelpers';
import Collapse from 'antd/lib/collapse';
import Releases from '../../Releases';
import { BucketStatus } from '../../../types';
import OTATag from '../../OTATag';
import { BucketAccessIconTooltip, BuildBucketAvatar } from '../CommonComponents';
import { buildBucketSort, getIsBuildDistroSupportedPlatform, getIsOTAPlatform } from '../../../helpers';
import { useAppPlatform } from '@/utils/hooks/helpers';
import { ReleaseCalendar } from '@/components/ReleaseCalendar';
import { ValidatedAndLoadedReleaseWrapper } from '@/components/ValidatedRouteComponents';
import { PlatformsLabel } from '@/components/sharedComponents/PlatformsLabel';

export const SettingsMenu: React.FC<AppComponentProps> = () => {
    // coming soon
    return <div />;
};

export const ReleaseMenu: React.FC<AppComponentProps> = () => {
    // coming soon
    return <div />;
};

export enum AppMenuOption {
    Overview = 'overview',
    Rollouts = 'rollouts',
    Releases = 'releases',
    Builds = 'builds',
    FeatureFlags = 'feature-flags',
}

export type SubMenuOption = string;

export const displayTextForMenuOption = (option: AppMenuOption) => {
    switch (option) {
        case AppMenuOption.Overview:
            return 'Overview';
        case AppMenuOption.Rollouts:
            return 'Rollouts';
        case AppMenuOption.Releases:
            return 'Releases';
        case AppMenuOption.Builds:
            return 'Builds';
        case AppMenuOption.FeatureFlags:
            return 'Feature Flags';
    }
};

export const iconForMenuOption = (option: AppMenuOption, isSelected: boolean) => {
    const style: React.CSSProperties = {
        color: isSelected ? antGrey.grey1 : antGrey.grey7,
    };

    switch (option) {
        case AppMenuOption.Overview:
            return <CalendarFilled style={style} />;
        case AppMenuOption.Rollouts:
            return <SignalFilled style={style} />;
        case AppMenuOption.Releases:
            return <HistoryOutlined style={style} />;
        case AppMenuOption.Builds:
            return <AppstoreAddOutlined style={style} />;
        case AppMenuOption.FeatureFlags:
            return <FlagFilled style={style} />;
    }
};

export const routeForOption = (option: AppMenuOption, orgId: string, appId: string) => {
    switch (option) {
        case AppMenuOption.Overview:
            return createAppOverviewRoute(orgId, appId);
        case AppMenuOption.Rollouts:
            return createAppRolloutsRoute(orgId, appId);
        case AppMenuOption.Builds:
            return createAppBuildsRoute(orgId, appId);
        case AppMenuOption.Releases:
            return createAppReleasesRoute(orgId, appId);
        case AppMenuOption.FeatureFlags:
            return createAppFeatureFlagsRoute(orgId, appId);
    }
};

export const MenuItem: React.FC<{
    title: React.ReactNode;
    icon: React.ReactNode;
    shouldBeOpenByDefault?: boolean;
    isSelected: boolean;
    onClick: () => void;
    isChildMenuItem?: boolean;
    content?: React.ReactNode;
}> = ({ title, icon, content, onClick, isSelected, shouldBeOpenByDefault, isChildMenuItem }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const isMobile = useIsMobile();

    const itemStyle: React.CSSProperties = {
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    };

    React.useEffect(() => {
        if (shouldBeOpenByDefault) {
            setIsOpen(true);
        }
    }, [shouldBeOpenByDefault]);

    const titleComponent = (
        <div
            style={{
                margin: 0,
                padding: isMobile ? '18px 16px' : '12px 16px',
                backgroundColor: isChildMenuItem && isSelected ? antGrey.grey5 : 'transparent',
            }}
            onClick={() => {
                if (!isOpen) {
                    setIsOpen(true);
                }
                onClick();
            }}
        >
            <div
                style={{
                    paddingLeft: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 22,
                }}
            >
                {icon}
                {typeof title === 'string' ?
                    <Text style={{ fontSize: 16, fontWeight: 600, color: antGrey.grey8 }}>{title}</Text>
                :   title}
            </div>
        </div>
    );

    const collapseWrapper = (children: React.ReactNode) => (
        <Collapse
            defaultActiveKey={shouldBeOpenByDefault ? ['1'] : []}
            activeKey={isOpen ? '1' : undefined}
            ghost
            expandIconPosition="end"
            collapsible="icon"
            style={{
                padding: 0,
                margin: 0,
            }}
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey7,
                        fontSize: 14,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            destroyInactivePanel={false}
            onChange={() => setIsOpen(!isOpen)}
            className={isSelected ? 'menu-item-collapse-selected' : 'menu-item-collapse'}
            items={[
                {
                    key: '1',
                    label: titleComponent,
                    children: children,
                    headerClass:
                        isSelected ? 'app-menu-item-selected'
                        : isChildMenuItem ? 'app-menu-item-child'
                        : 'app-menu-item',
                    forceRender: true,
                    style: {
                        padding: 0,
                        ...itemStyle,
                    },
                },
            ]}
        />
    );

    return content ? collapseWrapper(content) : titleComponent;
};

export const AppMenu: React.FC<
    AppComponentProps & {
        isCollapsed: boolean;
    }
> = ({ appId, orgId, isCollapsed }) => {
    const isMobile = useIsMobile();
    const history = useHistory();

    const appBuckets = useSelector((state: RootState) => selectBuildDistributionBucketsForAppId(state, appId)) ?? [];
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isBuildDistroEnabled = useSelector((state: RootState) => selectIsBuildDistroEnabled(state, appId));
    const shouldDefaultToBuildDistro = useSelector(
        (state: RootState) => selectAppSettings(state, appId).shouldDefaultToBuildDistro,
    );
    const appPlatform = useAppPlatform({ orgId, appId });
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, appId).appImageUrl);

    const matchesReleaseCalendar = useRouteMatch([routes.appReleaseCalendar]);
    const matchesHomeOrReleases = useRouteMatch([routes.app, routes.release]);
    const matchesBuilds = useRouteMatch([routes.appBuilds]);
    const matchesSharedWithMe = useRouteMatch([routes.sharedWithMeBucket]);
    const sharedWithMeKey = 'shared-with-me';
    const releaseCalendarKey = 'release-calendar';

    const matchesBucket = useRouteMatch<{
        bucketIdParam: string;
    }>([routes.appBucket]);

    const selectedOption =
        matchesBucket || matchesBuilds || matchesSharedWithMe ? AppMenuOption.Builds
        : matchesHomeOrReleases ? AppMenuOption.Releases
        : null;

    const selectedSubOption =
        matchesBucket ? matchesBucket.params.bucketIdParam
        : matchesSharedWithMe ? sharedWithMeKey
        : matchesReleaseCalendar ? releaseCalendarKey
        : null;

    const isOTAPlatform = getIsOTAPlatform(appPlatform);
    const isPlatformBuildDistroSupported = getIsBuildDistroSupportedPlatform(appPlatform);

    const MENU_OPTIONS =
        isMobile ? [AppMenuOption.Builds]
        : isPlatformBuildDistroSupported ? [AppMenuOption.Builds, AppMenuOption.Releases]
        : [AppMenuOption.Releases];

    appBuckets.sort(buildBucketSort);

    return (
        <div
            style={{
                width: '100%',
                flexShrink: 0,
            }}
        >
            {!isMobile && (
                <div
                    style={{
                        ...styles.timelineContent,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingTop: 10,
                        paddingBottom: 36,
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        {isOTAPlatform && <OTATag />}
                        <PlatformsLabel appId={appId} platform={appPlatform} appStoreType={appStoreType} />
                    </div>
                </div>
            )}
            {MENU_OPTIONS.map((option) => {
                const isSelected = selectedOption === option && !selectedSubOption;
                let shouldBeOpenByDefault = false;
                switch (option) {
                    case AppMenuOption.Builds:
                        shouldBeOpenByDefault = shouldDefaultToBuildDistro ? true : isBuildDistroEnabled;
                        break;
                    case AppMenuOption.Releases:
                        shouldBeOpenByDefault = !shouldDefaultToBuildDistro;
                        break;
                }

                return (
                    <MenuItem
                        key={option}
                        shouldBeOpenByDefault={shouldBeOpenByDefault}
                        isSelected={isSelected}
                        onClick={() => {
                            if (!isMobile) {
                                history.push(routeForOption(option, orgId, appId), { [ROUTER_REDIRECT]: true });
                            }
                        }}
                        title={displayTextForMenuOption(option)}
                        icon={iconForMenuOption(option, isSelected)}
                        content={(() => {
                            switch (option) {
                                case AppMenuOption.Builds:
                                    if (!isBuildDistroEnabled || appBuckets.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <div
                                            style={{
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                paddingTop: 16,
                                                paddingBottom: 30,
                                            }}
                                        >
                                            {appBuckets.map((bucket) => {
                                                if (bucket.status === BucketStatus.Archived) {
                                                    return null;
                                                }

                                                return (
                                                    <MenuItem
                                                        icon={null}
                                                        key={bucket.id}
                                                        isSelected={selectedSubOption === bucket.id}
                                                        isChildMenuItem={true}
                                                        onClick={() => {
                                                            history.push(
                                                                createAppBuildDistroBucketRoute(
                                                                    orgId,
                                                                    appId,
                                                                    bucket.id,
                                                                ),
                                                            );
                                                        }}
                                                        title={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 16,
                                                                    paddingLeft: 12,
                                                                }}
                                                            >
                                                                <BuildBucketAvatar
                                                                    appImageUrl={appImageUrl}
                                                                    size={12}
                                                                    bucket={bucket}
                                                                />
                                                                <Text
                                                                    style={{
                                                                        fontSize: 14,
                                                                        fontWeight: 400,
                                                                        color: antGrey.grey8,
                                                                    }}
                                                                >
                                                                    {bucket.name}
                                                                </Text>

                                                                {(bucket.orgWideAccessEnabled ||
                                                                    bucket.publicBucketLinkEnabled) && (
                                                                    <BucketAccessIconTooltip
                                                                        bucketHasOrgWideAccess={
                                                                            bucket.orgWideAccessEnabled
                                                                        }
                                                                        isExternalBucketView={
                                                                            bucket.publicBucketLinkEnabled
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        }
                                                    />
                                                );
                                            })}
                                            <MenuItem
                                                icon={null}
                                                key={sharedWithMeKey}
                                                isSelected={selectedSubOption === sharedWithMeKey}
                                                isChildMenuItem={true}
                                                onClick={() => {
                                                    history.push(createAppSharedWithMeBucketRoute(orgId, appId));
                                                }}
                                                title={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 16,
                                                            paddingLeft: 12,
                                                        }}
                                                    >
                                                        <BlockOutlined
                                                            style={{
                                                                color: antGrey.grey6,
                                                                fontSize: 12,
                                                                width: 19,
                                                            }}
                                                        />
                                                        <Text
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 400,
                                                                color: antGrey.grey8,
                                                            }}
                                                        >
                                                            Shared with me
                                                        </Text>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    );
                                case AppMenuOption.Releases:
                                    return (
                                        <div>
                                            <MenuItem
                                                icon={null}
                                                key={releaseCalendarKey}
                                                isSelected={selectedSubOption === releaseCalendarKey}
                                                isChildMenuItem={true}
                                                onClick={() => {
                                                    history.push(createAppReleaseCalendarRoute(orgId, appId));
                                                }}
                                                title={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 16,
                                                        }}
                                                    >
                                                        <CalendarOutlined
                                                            style={{
                                                                color:
                                                                    selectedSubOption == releaseCalendarKey ?
                                                                        antGrey.grey7
                                                                    :   antGrey.grey6,
                                                                fontSize: 12,
                                                                width: 19,
                                                            }}
                                                        />
                                                        <Text
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 400,
                                                                color: antGrey.grey8,
                                                            }}
                                                        >
                                                            Release schedule
                                                        </Text>
                                                    </div>
                                                }
                                            />
                                            <Releases isCollapsed={isCollapsed} appId={appId} orgId={orgId} />
                                        </div>
                                    );

                                case AppMenuOption.Overview:
                                case AppMenuOption.Rollouts:
                                case AppMenuOption.FeatureFlags:
                                    return null;
                            }
                        })()}
                    />
                );
            })}
        </div>
    );
};

export type AppMenusSidebarProps = AppComponentProps;

export const AppMenusSidebar: React.FC<AppMenusSidebarProps> = (props) => {
    const { appId } = props;

    const appName = useSelector((state: RootState) => selectAppName(state, appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    return (
        <div
            style={{
                ...styles.timelineStyle,
                justifyContent: 'flex-start',
            }}
        >
            <div
                style={{
                    padding: 20,
                    backgroundColor: antGrey.grey4,
                }}
            >
                <Text
                    style={{
                        fontSize: 16,
                        color: antGrey.grey8,
                        width: '100%',
                    }}
                    ellipsis
                >
                    <strong>{appName}</strong> ({displayTextForAppPlatform(platform)})
                </Text>
            </div>
            <Switch>
                <Route
                    path={routes.release}
                    render={(routeProps: RouteComponentProps) => {
                        const releaseRouteProps = routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                        return (
                            <ValidatedAndLoadedReleaseWrapper {...releaseRouteProps} withTracking>
                                {(validatedReleaseRouteProps) => {
                                    return <ReleaseMenu {...validatedReleaseRouteProps} />;
                                }}
                            </ValidatedAndLoadedReleaseWrapper>
                        );
                    }}
                />
                <Route path={routes.appSettings} render={() => <SettingsMenu {...props} />} />
                <Route path={routes.app} render={() => <AppMenu {...props} isCollapsed={false} />} />
                <Route
                    path={routes.appReleaseCalendar}
                    render={() => {
                        return <ReleaseCalendar orgId={props.orgId} appId={props.appId} />;
                    }}
                />{' '}
            </Switch>
        </div>
    );
};
